"use client";

import { useEffect, useState } from "react";
import {
  EMPTY_FORM,
  FormData,
  QCM_STEPS,
  QcmStep,
  TOTAL_STEPS,
  isValidEmail,
  isValidPhone,
} from "@/lib/form";
import { trackEvent, identifyLead } from "@/lib/analytics";
import { sendLead } from "@/lib/lead";
import { TRACKING_EVENTS } from "@/lib/posthog";

// ============================================================================
// Hook du formulaire de qualification.
//   Encapsule l'état (étape, données, "touched"), la validation du contact, et
//   les effets de bord des 2 temps de capture :
//     • submitContact → identify + webhook partial_submission + events
//     • selectQcm      → events par étape, puis onComplete() à la dernière
//   Le composant ne fait plus que de l'affichage.
// ============================================================================

export type QualificationForm = {
  step: number; // 0 = contact, 1..N = QCM
  data: FormData;
  touched: boolean;
  contactValid: boolean;
  progress: number; // 0..100
  currentQcm: QcmStep | null; // null sur l'étape contact
  setField: (patch: Partial<FormData>) => void;
  submitContact: () => void;
  selectQcm: (key: keyof FormData, value: string) => void;
  back: () => void;
};

export function useQualificationForm(
  onComplete: (data: FormData) => void
): QualificationForm {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<FormData>(EMPTY_FORM);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    trackEvent(TRACKING_EVENTS.FORM_OPENED);
  }, []);

  const setField = (patch: Partial<FormData>) =>
    setData((d) => ({ ...d, ...patch }));

  const contactValid =
    data.nomComplet.trim().length > 2 &&
    isValidEmail(data.email) &&
    isValidPhone(data.phone);

  const submitContact = () => {
    setTouched(true);
    if (!contactValid) return;
    // Capture du lead au plus tôt (avant la qualification).
    identifyLead(data.email, {
      nom_complet: data.nomComplet,
      phone: data.phone,
    });
    // Envoi webhook (temps 1) : contact seul, avant le QCM.
    sendLead("partial_submission", data);
    trackEvent(TRACKING_EVENTS.FORM_PARTIAL_SUBMISSION, { email: data.email });
    trackEvent(TRACKING_EVENTS.FORM_STEP_COMPLETED, { step: "contact" });
    setTouched(false);
    setStep(1);
  };

  const selectQcm = (key: keyof FormData, value: string) => {
    const next = { ...data, [key]: value };
    setData(next);
    trackEvent(TRACKING_EVENTS.FORM_STEP_COMPLETED, { step: key, value });

    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else {
      onComplete(next); // dernière étape → le parent qualifie / disqualifie
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const progress = Math.round((step / (TOTAL_STEPS - 1)) * 100);
  const currentQcm = step > 0 ? QCM_STEPS[step - 1] : null;

  return {
    step,
    data,
    touched,
    contactValid,
    progress,
    currentQcm,
    setField,
    submitContact,
    selectQcm,
    back,
  };
}
